/*
 * Confidential and Proprietary.
 * Do not distribute without Bloom Harbor Gifts consent.
 * Copyright Bloom Harbor Gifts 2019. All rights reserved.
 */

import { useSelector } from 'react-redux';
import { useQuery } from '@apollo/client';

import { getBrandName } from '../../../state/ducks/App/ducks/Brand/Brand-Selectors';
import { getIsBot } from '../../../state/ducks/App/App-Selectors';
import useGetAccessTokenSafely from '../../gql/hooks/useGetAccessTokenSafely';
import { GRAPHQL_ENV } from '../../gql';
import useBrowserUUID from '../useBrowserUUID';
import { getFeatureFlag } from '../../../state/ducks/App/ducks/Config/Config-Selectors';
import findPersonalizedReminders from '../../gql/queries/findPersonalizedReminders';

/**
 * @description - used to communicate with customer-personalize API for personalized content.
 * @param { string } queryName
 * @param {boolean} optInHoldoutParticipation - overrides holdout participation for specific UI/Components
 * @param {boolean} resolveIdentity - boolean to include salesforce identity in api
 * @param { object } apolloOptions - overrides | additional options to pass to useQuery API
 * @returns { object } - { loading, error, data, variables } -> add variables.skip to loading handling
 */

const useGetDerivedReminders = ({
    queryName = 'RemindersQuery',
    optInHoldoutParticipation = false,
    resolveIdentity = false,
    apolloOptions = {},
}) => {
    const isBot = useSelector(getIsBot);
    const isSalesForceRecsEnabled = useSelector(getFeatureFlag('is-salesforce-recs-enabled'));

    if (isBot || !isSalesForceRecsEnabled) {
        return {
            loading: false, error: false, data: null, variables: { skip: false },
        };
    }

    const query = findPersonalizedReminders(queryName);
    const brand = useSelector(getBrandName) || '1800flowers';
    const isPersonalizationHoldoutEnabled = optInHoldoutParticipation ? useSelector(getFeatureFlag('is-personalization-holdout-enabled')) : optInHoldoutParticipation;
    const browserUUID = useBrowserUUID();

    const jwt = useGetAccessTokenSafely();
    // skip until jwt is loading
    const skip = Boolean(!jwt || !browserUUID);

    return useQuery(query, {
        variables: (skip) ? { skip } : {
            // use default variables directly
            brand,
            environment: GRAPHQL_ENV,
            isHoldoutEligible: isPersonalizationHoldoutEnabled,
            resolveIdentity,
            user: {
                anonymousId: browserUUID,
            },
        },
        context: { api: 'personalizedContent', jwt },
        fetchPolicy: 'no-cache',
        skip,
        ...apolloOptions,
    });
};

export default useGetDerivedReminders;
